import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { AssetFormSchema } from '../../../types/field-builder';

interface VersionHistoryProps {
  onLoadVersion: () => void;
}

export default function VersionHistory({ onLoadVersion }: VersionHistoryProps) {
  const [versions, setVersions] = useState<AssetFormSchema[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVersions();
  }, []);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/field-builder-operations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ action: 'list_versions' }),
        }
      );

      const result = await response.json();

      if (result.success) {
        setVersions(result.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar versões:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishVersion = async (id: string) => {
    if (!confirm('Deseja publicar esta versão? Ela se tornará a versão ativa do formulário.')) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/field-builder-operations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'publish_version',
            data: { id }
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        alert('✅ Versão publicada com sucesso!');
        loadVersions();
        onLoadVersion();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Erro ao publicar versão:', error);
      alert('❌ Erro ao publicar versão');
    }
  };

  const handleDeleteVersion = async (id: string, isActive: boolean) => {
    if (isActive) {
      alert('Não é possível excluir a versão ativa');
      return;
    }

    if (!confirm('Deseja realmente excluir esta versão?')) {
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/field-builder-operations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'delete_version',
            data: { id }
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        alert('✅ Versão excluída com sucesso!');
        loadVersions();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Erro ao excluir versão:', error);
      alert('❌ Erro ao excluir versão');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Carregando histórico...</p>
        </div>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <i className="ri-history-line text-6xl text-gray-300 mb-4"></i>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Nenhuma versão encontrada
        </h3>
        <p className="text-gray-600">
          Salve uma configuração para criar a primeira versão
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Histórico de Versões</h2>
        <p className="text-sm text-gray-600 mt-1">
          Visualize e gerencie todas as versões do formulário
        </p>
      </div>

      <div className="divide-y divide-gray-200">
        {versions.map((version) => (
          <div key={version.id} className="p-6 hover:bg-gray-50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Versão {version.version}
                  </h3>
                  {version.is_active && (
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                      Ativa
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                  <div>
                    <span className="font-medium">Criado em:</span>{' '}
                    {new Date(version.created_at).toLocaleString('pt-BR')}
                  </div>
                  <div>
                    <span className="font-medium">Atualizado em:</span>{' '}
                    {new Date(version.updated_at).toLocaleString('pt-BR')}
                  </div>
                </div>

                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <i className="ri-folder-line"></i>
                    <span>{version.schema.tabs?.length || 0} abas</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-file-list-line"></i>
                    <span>{version.schema.fields?.length || 0} campos</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    <span>
                      {version.schema.fields?.filter(f => f.required).length || 0} obrigatórios
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 ml-4">
                {!version.is_active && (
                  <button
                    onClick={() => handlePublishVersion(version.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-checkbox-circle-line"></i>
                    Publicar
                  </button>
                )}
                {!version.is_active && (
                  <button
                    onClick={() => handleDeleteVersion(version.id, version.is_active)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
